import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, CheckCircle2, Circle, Clock, ListTodo } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";

const SECTIONS = [
  { key: "fait", title: "Réalisé", icon: CheckCircle2, color: "text-green-600", ring: "bg-green-100 text-green-700" },
  { key: "attente", title: "En attente / action requise", icon: Clock, color: "text-amber-600", ring: "bg-amber-100 text-amber-700" },
  { key: "backlog", title: "À venir / idées", icon: ListTodo, color: "text-brand-accent", ring: "bg-brand-cream text-brand-ink" },
];

export default function ProjectTracker() {
  const [tasks, setTasks] = useState(null);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("backlog");
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/admin/project-tasks").then(({ data }) => setTasks(data));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    setTasks((prev) => prev.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)));
    try { await api.patch(`/admin/project-tasks/${t.id}`, { done: !t.done }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    try { await api.delete(`/admin/project-tasks/${t.id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const add = async () => {
    if (!label.trim()) return;
    setAdding(true);
    try { await api.post("/admin/project-tasks", { category, label: label.trim() }); setLabel(""); load(); toast.success("Tâche ajoutée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  if (!tasks) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-brand-accent" /></div>;

  const done = tasks.filter((t) => t.done).length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="project-tracker">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Suivi du projet</h1>
        <p className="text-brand-sub text-sm mt-1">Avancement de la plateforme Noumabou. Cochez les tâches terminées, ajoutez les vôtres.</p>
      </div>

      <div className="bg-white border border-brand-line rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-brand-ink">Progression globale</span>
          <span data-testid="progress-pct" className="font-heading font-black text-2xl text-brand-accent">{pct}%</span>
        </div>
        <div className="h-3 rounded-full bg-brand-cream overflow-hidden">
          <div className="h-full rounded-full bg-brand-accent transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-brand-muted mt-2">{done} / {tasks.length} tâches terminées</p>
      </div>

      <div className="bg-white border border-brand-line rounded-2xl p-4 flex flex-col sm:flex-row gap-2">
        <input data-testid="new-task-input" className={inputCls} placeholder="Nouvelle tâche…" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <select data-testid="new-task-category" className={`${inputCls} sm:w-52`} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="backlog">À venir / idées</option>
          <option value="attente">En attente</option>
          <option value="fait">Réalisé</option>
        </select>
        <button onClick={add} disabled={adding} data-testid="add-task-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-5 py-2.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60 shrink-0">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
        </button>
      </div>

      {SECTIONS.map((s) => {
        const list = tasks.filter((t) => t.category === s.key);
        if (list.length === 0) return null;
        return (
          <div key={s.key} data-testid={`section-${s.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <h2 className="font-heading font-bold text-lg text-brand-ink">{s.title}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.ring}`}>{list.length}</span>
            </div>
            <div className="space-y-2">
              {list.map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="bg-white border border-brand-line rounded-xl px-4 py-3 flex items-center gap-3 group">
                  <button onClick={() => toggle(t)} data-testid={`toggle-${t.id}`} className="shrink-0">
                    {t.done ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Circle className="w-5 h-5 text-brand-muted" />}
                  </button>
                  <span className={`flex-1 text-sm ${t.done ? "line-through text-brand-muted" : "text-brand-ink"}`}>{t.label}</span>
                  <button onClick={() => remove(t)} data-testid={`delete-task-${t.id}`} className="p-1.5 text-brand-secondary hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
